
import _ from 'lodash';
import { mapGetters, mapState, mapActions, mapMutations } from 'vuex';
import TheWatermark from '@/views/common/TheWatermark';
import WidgetType from '@/enums/WidgetType';
import WidgetStyle from '@/enums/WidgetStyle';
import WidgetLocale from '@/enums/WidgetLocale';
import { focusFirstElement, setFocusTrapping } from '@/lib/popupFocus';
import DataAnalytics from '@/mixins/DataAnalytics';
import { isIframe } from '@/lib/utils';

const OPTION_PREFIX = 'option_';
const PRODUCT_OPTION_PREFIX = 'product_option_';

export default {
  name: 'TheWidget',
  components: { TheWatermark },
  mixins: [DataAnalytics],
  props: {
    widgetType: {
      type: Number,
      required: true,
      validator: v => WidgetType.hasValue(v)
    }
  },
  data() {
    return {
      isLoading: true,
      debouncedUpdateScreenWidth: null,
      hasScrolledToCurrentWidget: false
    };
  },
  computed: {
    ...mapState(['api']),
    ...mapState('session', [
      'sessionParams',
      'currentUser',
      'isLoadingSession'
    ]),
    ...mapState('reviewSettings', ['reviewSettings']),
    ...mapState('widget', ['widget', 'isMobile', 'iframeId']),
    ...mapState('window', ['windowService']),
    ...mapState('userProperties', ['isUserPropertyFilterShowable']),
    ...mapState('category', ['displayableWithCategory']),
    ...mapState('review', ['isReviewWriteWhitelisted']),
    ...mapState('translation', ['locale']),
    ...mapGetters(['isWidgetEnvironmentShop']),
    ...mapGetters('widget', [
      'noItemActionRequired',
      'isFullscreenPopup',
      'isMobileViewport'
    ]),
    topbarDisplayable() {
      return (
        !isIframe() &&
        this.isWidgetEnvironmentShop &&
        this.isMobile &&
        this.widgetDisplayable
      );
    },
    maxWidth() {
      const isResponsive =
        WidgetStyle.RESPONSIVE_STYLES.includes(this.widget?.widget_style) ||
        this.widgetType === WidgetType.REVIEW_PROMPT;
      const isMobile = isResponsive ? this.isMobileViewport : this.isMobile;

      return isMobile ? '720px' : '2560px';
    },
    widgetFontFamilyCss() {
      const { widget_english_number_font_name, korean_font_name } =
        this.widget.widget_fonts_map || {};

      let fontNames = [
        widget_english_number_font_name,
        this.locale === WidgetLocale.KO_KR ? korean_font_name : null,
        ...WidgetLocale.defaultFontFamily(this.locale)
      ]
        .filter(Boolean)
        .map(v => `'${v}'`)
        .join(', ');

      return `
body {
  font-family: ${fontNames};
}
      `.trim();
    },
    widgetFontCss() {
      return `
${this.koreanFontUrl}

${this.englishNumberFontFace}
      `.trim();
    },
    koreanFontUrl() {
      const { korean_font_url } = this.widget.widget_fonts_map || {};
      return korean_font_url && this.locale === WidgetLocale.KO_KR
        ? `@import url('${korean_font_url}');`
        : '';
    },
    englishNumberFontFace() {
      const {
        widget_english_number_font_name,
        widget_english_number_font_url_map
      } = this.widget.widget_fonts_map || {};
      if (!widget_english_number_font_name) return '';

      return `
@font-face {
  font-family: '${widget_english_number_font_name}';
  font-style: normal;
  font-weight: 400;
  font-display: swap;
  src: url('${widget_english_number_font_url_map.normal}') format('woff2');
  unicode-range: U+0041-005A, U+0061-007A, U+0030-0039;
}

@font-face {
  font-family: '${widget_english_number_font_name}';
  font-style: normal;
  font-weight: 700;
  font-display: swap;
  src: url('${widget_english_number_font_url_map.bold}') format('woff2');
  unicode-range: U+0041-005A, U+0061-007A, U+0030-0039;
}
      `.trim();
    },
    mainColorCss() {
      const { main_color } = this.widget;
      if (!main_color) return '';

      // HACK: 메인 컬러가 있는 위젯인 경우는 클래스 명으로 main-color, main-background-color 를 이용한다.
      return `
.main-color {
  color: ${main_color};
}
.main-background-color {
  background-color: ${main_color};
}
      `.trim();
    },
    widgetTextSizeAdjustCss() {
      return `
body {
  -moz-text-size-adjust: 100%;
  -webkit-text-size-adjust: 100%;
  text-size-adjust: 100%;
}
      `.trim();
    },
    widgetDisplayable() {
      return (
        this.widget &&
        this.widget.widget_displayable &&
        this.displayableWithCategory
      );
    },
    shouldShowItems() {
      if (!this.noItemActionRequired) return true;

      const isReviewWriteWhitelisted = this.reviewSettings.review_form_enabled
        ? this.currentUser?.review_write_whitelisted
        : this.isReviewWriteWhitelisted;

      return (
        isReviewWriteWhitelisted &&
        this.widget.widget_type === WidgetType.PRODUCT_REVIEWS &&
        [WidgetStyle.LIST_V3, WidgetStyle.BOARD].includes(
          this.widget.widget_style
        )
      );
    },
    scrollToCurrentWidget() {
      return (
        this.$route.query['crema-scroll-to-widget-id'] * 1 === this.widget?.id
      );
    }
  },
  created() {
    this.initWindowService(this.$router);
    const {
      product_code,
      widget_id,
      parent_widget_id,
      iframe_id,
      preview,
      dummy,
      original_widget_id,
      locale,
      category_code,
      parent_url,
      install_method,
      media_index,
      media_reviews_first,
      category_id,
      scores,
      sort,
      unlinkable,
      query,
      with_video,
      cohort_item_id,
      expires_since,
      expires_until,
      widget_env,
      parent_widget_type,
      ...restQuery
    } = this.$route.query;
    const { brand_code } = this.$route.params;
    const review_id =
      this.$route.params.review_id || this.$route.query.review_id;
    const { isMobile, widgetStyle } = this.parsePath(this.$route.path);
    const reviewOptions = this.extractOptions(OPTION_PREFIX, restQuery);
    const productOptions = this.extractOptions(
      PRODUCT_OPTION_PREFIX,
      restQuery
    );
    this.setSessionParams(this.$route.query);
    this.initApi({
      brandCode: brand_code,
      sessionParams: this.sessionParams,
      preview: preview,
      dummy: dummy,
      locale: locale
    });
    this.SET_PREVIEW(!!preview);
    this.SET_BRAND_CODE(brand_code);
    this.SET_PRODUCT_CODE(product_code);
    this.SET_CATEGORY_CODE(category_code);
    this.SET_UNLINKABLE(unlinkable);
    this.SET_MEDIA_INDEX(media_index);
    this.SET_MEDIA_REVIEWS_FIRST(media_reviews_first);
    this.SET_IS_MOBILE(isMobile);
    this.SET_IFRAME_ID(iframe_id);
    this.SET_REVIEW_ID(review_id);
    this.SET_CATEGORY_ID(category_id);
    this.SET_SCORES(scores);
    this.SET_SORT(sort);
    this.SET_QUERY(query);
    this.SET_WITH_VIDEO(with_video);
    this.SET_REVIEW_OPTIONS(reviewOptions);
    this.SET_PRODUCT_OPTIONS(productOptions);
    this.SET_COHORT_ITEM_ID(cohort_item_id);
    this.SET_EXPIRES_SINCE(expires_since);
    this.SET_EXPIRES_UNTIL(expires_until);
    this.SET_WIDGET_ENV(widget_env);
    this.SET_PARENT_WIDGET_TYPE(parent_widget_type);
    this.fetchSession();
    this.fetchTranslations(locale);
    this.fetchReviewSettings(isMobile);
    this.fetchWidget({
      id: widget_id,
      parent_widget_id: parent_widget_id,
      widget_type: this.widgetType,
      widget_style: widgetStyle,
      preview,
      original_widget_id,
      parent_url,
      install_method
    }).finally(() => {
      if (this.widget.id) {
        this.fetchDisplayableWithCategory({
          widget_id: this.widget.id,
          product_code,
          preview,
          category_code,
          widget_type: this.widgetType
        });
      }

      if (this.widget.show_size_filter)
        this.fetchUserPropertyFilterShowable({ product_code });

      if (
        this.widget.widget_style === WidgetStyle.LIST_V3 &&
        this.widget.use_ai_size_recommendation
      )
        this.fetchSizeRecommendationDisplayable({ product_code });

      this.updateScreenWidth();
      this.debouncedUpdateScreenWidth = _.debounce(this.updateScreenWidth, 100);
      window.addEventListener('resize', this.debouncedUpdateScreenWidth);
    });
  },
  methods: {
    ...mapActions('googleTranslator', ['initialize']),
    ...mapActions('window', ['initWindowService']),
    ...mapActions(['initApi']),
    ...mapActions('reviewSettings', ['fetchReviewSettings']),
    ...mapActions('widget', ['fetchWidget']),
    ...mapActions('session', ['setSessionParams', 'fetchSession']),
    ...mapActions('translation', ['fetchTranslations']),
    ...mapActions('category', ['fetchDisplayableWithCategory']),
    ...mapActions('userProperties', ['fetchUserPropertyFilterShowable']),
    ...mapActions('sizeRecommendation', ['fetchSizeRecommendationDisplayable']),
    ...mapMutations([
      'SET_PREVIEW',
      'SET_BRAND_CODE',
      'SET_UNLINKABLE',
      'SET_PRODUCT_CODE',
      'SET_CATEGORY_CODE',
      'SET_CATEGORY_ID',
      'SET_SCORES',
      'SET_SORT',
      'SET_QUERY',
      'SET_WITH_VIDEO',
      'SET_REVIEW_OPTIONS',
      'SET_PRODUCT_OPTIONS',
      'SET_COHORT_ITEM_ID',
      'SET_EXPIRES_SINCE',
      'SET_EXPIRES_UNTIL',
      'SET_WIDGET_ENV',
      'SET_PARENT_WIDGET_TYPE'
    ]),
    ...mapMutations('widget', [
      'SET_IS_MOBILE',
      'SET_IFRAME_ID',
      'SET_REVIEW_ID',
      'SET_DEVICE_VIEWPORT'
    ]),
    ...mapMutations('review', ['SET_MEDIA_REVIEWS_FIRST', 'SET_MEDIA_INDEX']),
    onReady() {
      this.insertStyle('main-color', this.mainColorCss);
      this.insertStyle('widget-font', this.widgetFontCss);
      this.insertStyle('widget-font-family', this.widgetFontFamilyCss);
      this.insertStyle('widget-text-size-adjust', this.widgetTextSizeAdjustCss);
      this.insertStyle('brand-style', this.reviewSettings.custom_css);
      this.insertStyle('widget-style', this.widget.vue_widget_css);

      this.insertScript('brand-script', this.reviewSettings.custom_js);
      this.insertScript('widget-script', this.widget.vue_widget_javascript);

      if (this.reviewSettings.search_engine_noindex) this.insertNoindexMeta();

      this.initTranslator();
      if (this.shouldShowItems) {
        this.windowService.setParentDivMargin(this.widget.margin_style);
      }
      this.windowService.setParentDivWidth(this.widget.custom_width);
      this.windowService.sendReady(this.iframeId);

      if (this.isFullscreenPopup) {
        this.$nextTick(() => {
          focusFirstElement();
          setFocusTrapping();
        });
      }

      this.isLoading = false;

      if (this.scrollToCurrentWidget && !this.hasScrolledToCurrentWidget) {
        this.hasScrolledToCurrentWidget = true;
        this.windowService.scrollTo(0);
      }
      this.$nextTick(() => this.analyze());
    },
    insertStyle(id, code) {
      if (!code) return;

      const element = document.createElement('style');
      element.id = id;
      element.innerHTML = code;
      document.head.appendChild(element);
    },
    insertScript(id, code) {
      if (!code) return;

      const element = document.createElement('script');
      element.id = id;
      element.innerHTML = `
        try {
          ${code}
        } catch (e) {
          console.error('Error in TheWidget.insertScript(id="${id}"):', e);
        }
      `.trim();
      document.head.appendChild(element);
    },
    insertNoindexMeta() {
      if (document.getElementById('noindex-meta')) return;

      const element = document.createElement('meta');
      element.id = 'noindex-meta';
      element.name = 'robots';
      element.content = 'noindex';
      document.head.appendChild(element);
    },
    initTranslator() {
      this.initialize();
      document.dispatchEvent(new Event('history:updated'));
    },
    parsePath(path) {
      // e.g. '/cafe23.com/reviews/gallery', '/cafe23.com/mobile/reviews/gallery'
      const pathTokens = path.split('/');
      const widgetStyle =
        pathTokens[2] === 'mobile' ? pathTokens[4] : pathTokens[3];
      return {
        isMobile: pathTokens[2] === 'mobile',
        widgetStyle: isNaN(widgetStyle) ? widgetStyle : null
      };
    },
    extractOptions(prefix, query) {
      return Object.keys(query)
        .filter(key => key.startsWith(prefix))
        .reduce((acc, key) => {
          acc[key] = query[key];
          return acc;
        }, {});
    },
    updateScreenWidth() {
      const width = window.innerWidth;
      const viewport = width < 720 ? 'mobile' : width >= 1024 ? 'pc' : 'tablet';
      this.SET_DEVICE_VIEWPORT(viewport);
    },
    onClickTopbarClose() {
      this.windowService.backToReferrer();
    }
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.debouncedUpdateScreenWidth);
    this.debouncedUpdateScreenWidth.cancel();
  }
};
